import { useTranslation } from 'react-i18next';

import { useTheme } from 'styled-components';

import { ColorName } from 'types';

import { useAppSelector } from 'redux/hooks';

import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { Modal } from 'components/Modal';
import { OrderAddressPicker } from 'components/OrderAddressBlocks';
import { SelectWithControl } from 'components/SelectWithControl';

import { useDuplicateOrder } from './DuplicateOrderModal.hooks';
import * as S from './DuplicateOrderModal.styles';

type DuplicateOrderModalProps = {
  orderId: string;
  customerCode: string;
  customerName?: string;
  onClose: () => void;
};

function DuplicateOrderModal(props: DuplicateOrderModalProps): React.ReactElement {
  const { orderId, customerCode, customerName, onClose } = props;
  const { t } = useTranslation();
  // Catalogue colour (black / red / blue) as in the New Order modal.
  const catalogueColor = (useAppSelector((state) => state.settings.barColor) || undefined) as
    | ColorName
    | undefined;
  const theme = useTheme();

  const {
    control,
    setValue,
    zoneOptions,
    selectedZone,
    initialCustomer,
    initialAddresses,
    isPrefillingSet,
    isLoading,
    isPreparing,
    globalZone,
    successResult,
    handleZoneChange,
    handleGoToOrder,
    handleStayOnPage,
    onSubmit,
  } = useDuplicateOrder({ orderId, customerCode, customerName, onClose });

  if (successResult) {
    return (
      <Modal onClose={handleStayOnPage}>
        <S.Container>
          <S.SuccessTitle scale="xl3" isWrapped={true}>
            <S.SuccessIcon>
              <Icon name="FiCheckCircle" size={28} color="green" />
            </S.SuccessIcon>
            {t('order.copiedSuccessfully')}
          </S.SuccessTitle>

          <S.SuccessInfo
            fontFamily="LFT Zeno"
            fontWeight="extraLight"
            scale="s"
            isWrapped={true}
          >
            {t('order.copiedToZone', { zone: successResult.zone })}
          </S.SuccessInfo>

          <S.SuccessActions>
            <Button
              textTransform="isFirstLetterUppercase"
              fontFamily="LFT Zeno"
              fontWeight="light"
              motive="cancelTertiary"
              scale="s"
              onClick={handleStayOnPage}
            >
              {t('order.stayOnPage')}
            </Button>

            <Button
              textTransform="isFirstLetterUppercase"
              fontFamily="LFT Zeno"
              motive="actionPrimary"
              onClick={handleGoToOrder}
            >
              {t('order.goToOrder')}
            </Button>
          </S.SuccessActions>
        </S.Container>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <S.Container>
        <S.Title scale="xl3" $color={catalogueColor}>
          {t('order.copyTitle')}
        </S.Title>
        <S.Description>{t('order.copyDescription')}</S.Description>

        <S.Form onSubmit={onSubmit}>
          <S.ZoneField $color={catalogueColor}>
            <SelectWithControl
              required
              control={control}
              name="zone"
              label={t('order.commercialZone')}
              options={zoneOptions}
              onSelect={handleZoneChange}
              scale="xs"
              motive="filter"
              placeholder={t('common.selectPlaceholder')}
              defaultValue={globalZone}
              accentColor={catalogueColor ? theme.colors[catalogueColor] : undefined}
            />
          </S.ZoneField>

          <OrderAddressPicker
            control={control}
            setValue={setValue}
            zone={selectedZone}
            initialCustomer={initialCustomer}
            initialAddresses={initialAddresses}
            onPrefillingChange={isPrefillingSet}
            color={catalogueColor}
          />

          <S.ButtonWrapper>
            <S.CancelButton
              type="button"
              motive="actionSecondary"
              $color={catalogueColor}
              onClick={onClose}
            >
              {t('common.cancel')}
            </S.CancelButton>
            <S.ConfirmButton
              type="submit"
              motive="actionPrimary"
              $color={catalogueColor}
              disabled={isLoading || isPreparing}
              isLoading={isLoading}
            >
              {t('common.confirm')}
            </S.ConfirmButton>
          </S.ButtonWrapper>
        </S.Form>
      </S.Container>
    </Modal>
  );
}

export { DuplicateOrderModal };
